import React from 'react';
import { useSelector } from 'react-redux';
import { AREAS, ISettingArea, ISettingDescriptor, SETTINGS } from '../settings/registry';
import { RootState } from '../store/rootReducer';

export interface IVisibleSettings {
    /** Areas the viewer's effective claims permit seeing. */
    visibleAreas: ISettingArea[];
    /** Settings whose area, and own `permission` when narrower, the viewer's claims permit. */
    visibleSettings: ISettingDescriptor[];
    /**
     * Whether `effectiveClaims` has arrived from the server yet.
     *
     * `effectiveClaims` starts `undefined` on a cold load, not `[]` - an empty list is a real "no
     * permissions", `undefined` is "not yet known". `visibleAreas`/`visibleSettings` already treat
     * unknown the same as denied for anything gated by a permission, so nothing gated ever leaks
     * during the loading window; this flag is for callers (the settings search box) that want to
     * withhold even the permission-free entries until claims are confirmed, rather than showing a
     * momentarily-complete-looking list that then shrinks.
     */
    claimsLoaded: boolean;
}

/**
 * The claim-visible slice of the settings registry.
 *
 * `SettingsReferencePage` filters `AREAS`/`SETTINGS` by the viewer's effective claims so an
 * entitlement-gated feature (procurement, promos, recommendations, ...) is never listed for a
 * viewer who cannot see it in the product - `area.permission` is the same CASL claim the screen
 * itself is gated behind, and `setting.permission` narrows further for a field gated tighter than
 * its area. This hook is that same filter, factored out so any other registry consumer (the
 * settings search box) gets the identical privacy cut for free instead of re-deriving it.
 *
 * Reads directly from the same slice `useCan()` builds its ability from, rather than calling `useCan()`
 * in a loop over ~230 settings / ~106 areas - `useCan()` is itself a hook (`useSelector` inside), and
 * hooks cannot be called a variable number of times from a filter/map callback.
 *
 * Deliberately lives here, not in `src/settings/registry`: the registry stays free of React/Redux
 * so build scripts (`settings:check`, `settings:coverage`, `settings:manifest`) can import it
 * directly without a runtime.
 */
export const useVisibleSettings = (): IVisibleSettings => {
    const effectiveClaims = useSelector((state: RootState) => state.settings.effectiveClaims);
    const hasPermission = React.useCallback(
        (permission?: string) => !permission || (effectiveClaims ?? []).includes(permission),
        [effectiveClaims]
    );

    // Areas merged into another area's instance picker (globalCounterpart targets) stay out of
    // search and browse lists - they are reached through the merged picker's "Global" option.
    // Their settings and direct deep links keep working; only the duplicate row disappears.
    const mergedAwayKeys = React.useMemo(
        () => new Set(AREAS.map((a) => a.globalCounterpart).filter((k): k is string => !!k)),
        []
    );
    const visibleAreas: ISettingArea[] = React.useMemo(
        () => AREAS.filter((a) => hasPermission(a.permission) && !mergedAwayKeys.has(a.key)),
        [hasPermission, mergedAwayKeys]
    );
    const visibleAreaKeys = React.useMemo(
        () => new Set(AREAS.filter((a) => hasPermission(a.permission)).map((a) => a.key)),
        [hasPermission]
    );
    const visibleSettings: ISettingDescriptor[] = React.useMemo(
        () => SETTINGS.filter((s) => visibleAreaKeys.has(s.area) && hasPermission(s.permission)),
        [visibleAreaKeys, hasPermission]
    );

    return { visibleAreas, visibleSettings, claimsLoaded: effectiveClaims !== undefined };
};

export default useVisibleSettings;
